import * as React from "react";
import {
  FormControlLabel,
  Radio,
  styled,
  ToggleButtonGroup,
} from "@mui/material";
import {
  CustomFeedbackOption,
  CustomFeedbackSubOption,
} from "@kahunmedical/common-configuration/dist/configuration/dialogueOptions";
import ToggleButton from "@mui/lab/ToggleButton";
import { TranslateByGender } from "fe-common/locale/useTranslateByGender";

//region [[ Styles ]]

const CustomFeedbackView = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  background: theme.palette.primary.light,
  borderRadius: 8,
  padding: 12,
  fontSize: 13,
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    marginLeft: -44,
  },
}));

const CustomFeedbackQuestion = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  paddingTop: 8,
  borderTop: `1px dotted ${theme.palette.divider}`,
  "&:first-child": {
    paddingTop: 0,
    borderTop: "none",
  },
}));

const FeedbackButtons = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: 8,
  margin: "8px 0",
  [theme.breakpoints.down("sm")]: {
    margin: "6px 0",
    gap: 6,
  },
}));
const FeedbackButton = styled(FormControlLabel)(({ theme }) => ({
  flex: "1 1 calc(50% - 7px)",
  width: 220,
  maxWidth: "100%",
  color: theme.palette.text.primary + "99",
  margin: 0,
  fontSize: 13,

  padding: 6,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  gap: 6,
  "& span": {
    fontSize: 13,
    lineHeight: 1.3,
    padding: 0,
  },
  "&:hover": {
    color: theme.palette.text.primary,
    background: "rgba(0, 0, 0, 0.04)",
  },
  [theme.breakpoints.down("sm")]: {
    flex: "1 1 100%",
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]
export interface CustomFeedbackProps {
  customFeedbackOptions: CustomFeedbackOption[];
  value: CustomFeedbackOption[];
  rtlDisplay?: boolean;
  translate: TranslateByGender;
  onChange: (updatedFeedback: CustomFeedbackOption[]) => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const CustomFeedback = ({
  rtlDisplay,
  translate,
  customFeedbackOptions,
  value,
  onChange,
}: CustomFeedbackProps) => {
  const [customFeedback, setCustomFeedback] = React.useState<
    CustomFeedbackOption[]
  >(populateCustomFeedback(customFeedbackOptions, value));

  const subOptionUpdate = (
    subOption: CustomFeedbackSubOption,
    feedbackSubId: string,
  ): CustomFeedbackSubOption => {
    if (subOption.id === feedbackSubId) {
      return {
        ...subOption,
        value: true,
      };
    }
    return subOption;
  };

  const handleFeedbackChange = (
    feedbackId: string,
    feedbackSubId: string,
    value: boolean,
  ) => {
    const updatedFeedback: CustomFeedbackOption[] = customFeedback.map(
      (feedback) => {
        if (feedback.id === feedbackId) {
          if (feedbackSubId) {
            if (feedback.value) {
              return {
                ...feedback,
                subOptionsIfTrue: feedback.subOptionsIfTrue?.map((subOption) =>
                  subOptionUpdate(subOption, feedbackSubId),
                ),
                subOptionsIfFalse: undefined,
              };
            } else {
              return {
                ...feedback,
                subOptionsIfFalse: feedback.subOptionsIfFalse?.map(
                  (subOption) => subOptionUpdate(subOption, feedbackSubId),
                ),
                subOptionsIfTrue: undefined,
              };
            }
          } else
            return {
              ...feedback,
              value,
            };
        }
        return feedback;
      },
    );
    //strip undefined values
    const stripped = updatedFeedback
      .filter((option) => option.value !== undefined)
      .map((option) => {
        const requiredData = {
          id: option.id,
          text: option.text,
          value: option.value,
        };
        if (option.value && option.subOptionsIfTrue) {
          //strip undefined and false values from subOptions
          return {
            ...requiredData,
            subOptionsIfTrue: option.subOptionsIfTrue.filter(
              (subOption) => subOption.value,
            ),
          };
        } else if (option.subOptionsIfFalse) {
          return {
            ...requiredData,
            subOptionsIfFalse: option.subOptionsIfFalse.filter(
              (subOption) => subOption.value,
            ),
          };
        } else return requiredData;
      });

    onChange(stripped);
    setCustomFeedback(updatedFeedback);
  };

  return (
    <CustomFeedbackView dir={rtlDisplay ? "rtl" : ""}>
      {customFeedback.map((option) => {
        let subOptions = [];

        if (option.value) {
          subOptions = option.subOptionsIfTrue || [];
        } else if (option.value === false) {
          subOptions = option.subOptionsIfFalse || [];
        }

        return (
          <React.Fragment key={option.id}>
            <CustomFeedbackQuestion>
              <div>{option.text}</div>
              <ToggleButtonGroup
                color={"primary"}
                dir={"ltr"}
                value={option.value}
                size={"small"}
              >
                <ToggleButton
                  value={false}
                  onClick={() => handleFeedbackChange(option.id, null, false)}
                >
                  {translate("ANSWER_NO")}
                </ToggleButton>
                <ToggleButton
                  value={true}
                  onClick={() => handleFeedbackChange(option.id, null, true)}
                >
                  {translate("ANSWER_YES")}
                </ToggleButton>
              </ToggleButtonGroup>
            </CustomFeedbackQuestion>
            {subOptions.length > 0 && (
              <div>
                {option.value
                  ? option.subTitleIfTrue && (
                      <div> {option.subTitleIfTrue} </div>
                    )
                  : option.subTitleIfFalse && (
                      <div> {option.subTitleIfFalse} </div>
                    )}
                <FeedbackButtons>
                  {subOptions.map((subOption) => (
                    <FeedbackButton
                      control={
                        <Radio
                          value={!!subOption.value}
                          size={"small"}
                          checked={!!subOption.value}
                          onClick={() =>
                            handleFeedbackChange(
                              option.id,
                              subOption.id,
                              !subOption.value,
                            )
                          }
                        />
                      }
                      label={subOption.text}
                      key={"sub" + subOption.id}
                    />
                  ))}
                </FeedbackButtons>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </CustomFeedbackView>
  );
};
export const MemoizedCustomFeedback = React.memo(CustomFeedback);

function populateSubOptions(
  optionId: string,
  subOptions: CustomFeedbackSubOption[],
  subOptionsValues: CustomFeedbackSubOption[],
): CustomFeedbackSubOption[] {
  return subOptions.map((subOption) => {
    const subOptionValue = subOptionsValues?.find(
      (s) => s.id === `${optionId}.${subOption.id}`,
    );
    return {
      ...subOption,
      id: `${optionId}.${subOption.id}`,
      value: subOptionValue ? subOptionValue.value : subOption.value,
    };
  });
}

function populateCustomFeedback(
  customFeedbackOptions: CustomFeedbackOption[],
  customFeedbackValues: CustomFeedbackOption[],
): CustomFeedbackOption[] {
  if (!customFeedbackValues?.length) return customFeedbackOptions;

  return customFeedbackOptions.map((option) => {
    const optionValue = customFeedbackValues.find((o) => o.id === option.id);

    return {
      ...option,
      value: optionValue ? optionValue.value : option.value,
      subOptionsIfTrue:
        option.subOptionsIfTrue &&
        populateSubOptions(
          option.id,
          option.subOptionsIfTrue,
          optionValue.subOptionsIfTrue,
        ),
      subOptionsIfFalse:
        option.subOptionsIfFalse &&
        populateSubOptions(
          option.id,
          option.subOptionsIfFalse,
          optionValue.subOptionsIfFalse,
        ),
    };
  });
}
